import { assert, NormalizedNumber, UnixTimestamp } from '@sparkdotfi/common-universal'
import { useState } from 'react'
import { useChainId, useConfig } from 'wagmi'

import { getChainConfigEntry } from '@/config/chain'
import { useSnapshot } from '@/domain/hooks/useSnapshot'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { FinalizeSpkUnstakeObjective } from '@/features/actions/flavours/finalize-spk-unstake/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'
import { useSpkStakingData } from '@/features/spk/staking/logic/useSpkStakingData'

export interface UseFinishUnstakeDialogResult {
  spk: Token
  unstakeAmount: NormalizedNumber
  actionsViewState: ActionsViewState
  pageStatus: PageStatus
}

export function useFinalizeUnstakeDialog(): UseFinishUnstakeDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const chainId = useChainId()
  const { account } = useConnectedAddress()
  const wagmiConfig = useConfig()

  const sparkTokenFeatureConfig = getChainConfigEntry(chainId).spk
  assert(sparkTokenFeatureConfig, 'Spark token config should be defined')
  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const spk = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))

  const { spkStakingData } = useSpkStakingData({
    chainId,
    account,
    wagmiConfig,
    tokenRepository,
  })

  // @todo: useSnapshot used for success screen
  const [claimableWithdrawal] = useSnapshot(
    spkStakingData.withdrawals.find((w) => UnixTimestamp.fromDate(w.claimableAt).lte(spkStakingData.timestamp)),
  )

  const objectives: FinalizeSpkUnstakeObjective[] = claimableWithdrawal
    ? [
        {
          type: 'finalizeSpkUnstake',
          spk,
          amount: claimableWithdrawal.amount,
          epochs: claimableWithdrawal.epochs.map(Number),
        },
      ]
    : []

  const { viewState: actionsViewState } = useActions({
    objectives,
    context: {
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: true,
  })

  return {
    spk,
    unstakeAmount: claimableWithdrawal?.amount ?? NormalizedNumber.ZERO,
    actionsViewState,
    pageStatus: {
      state: pageStatus,
      actionsEnabled: true,
      goToSuccessScreen: () => setPageStatus('success'),
    },
  }
}
